import type { AddressInput, MetadataInput } from '@/graphql/saleor/api';

import ApplePayPaymentContact = ApplePayJS.ApplePayPaymentContact;
import type { AdyenCheckoutError } from '@adyen/adyen-web';

import type { OrderAppError } from '@/graphql/orderApp/api';
import { ACCOUNT_METADATA, ACCOUNT_METADATA_VALUE } from '@/lib/metadata/const';
import type { LocaleSlug } from '@/views/DeliveryView/helpers';
import {
  shippingPoBoxErrorsTranslations,
  shippingTerritoriesTranslations,
} from '@/views/DeliveryView/helpers';

export const makeAddressInputFromAppleContact = (
  contact: ApplePayPaymentContact,
  userAddresses?: any[],
  fillMissingWithMock = false,
  phoneNumber?: string,
): AddressInput => {
  const shouldAddToAddressBook: boolean =
    !fillMissingWithMock &&
    !!userAddresses &&
    userAddresses.some(
      (address) => address.postalCode === contact.postalCode, // Simply check the postal code
    );
  const metadata: MetadataInput[] = shouldAddToAddressBook
    ? [
        {
          key: ACCOUNT_METADATA.addressSaved,
          value: ACCOUNT_METADATA_VALUE.addressSaved,
        },
      ]
    : [];
  return {
    firstName: fillMissingWithMock ? 'MOCK' : contact?.givenName ?? '',
    lastName: fillMissingWithMock ? 'MOCK' : contact?.familyName ?? '',
    streetAddress1: fillMissingWithMock
      ? 'MOCK'
      : contact?.addressLines?.[0] ?? '',
    streetAddress2: contact?.addressLines?.[1] || '',
    city: contact?.locality || '',
    countryArea: contact?.administrativeArea || '',
    postalCode: contact?.postalCode || '',
    country: contact?.countryCode,
    phone: contact?.phoneNumber || phoneNumber,
    metadata,
    skipValidation: true,
  } as AddressInput;
};

export const makeApplePayErrorsFromAddressErrors = (
  addressErrors: OrderAppError[],
  locale = 'en',
): ApplePayError[] => {
  const localeSlug = locale as LocaleSlug;
  return addressErrors.map((error) => {
    switch (error.code) {
      case 'shipping_address.blacklisted':
        return new ApplePayError(
          'addressUnserviceable',
          'postalCode',
          shippingTerritoriesTranslations[localeSlug] || error.message,
        );
      case 'shipping_address.po_box_not_supported':
        return new ApplePayError(
          'addressUnserviceable',
          'postalCode',
          shippingPoBoxErrorsTranslations[localeSlug] || error.message,
        );
      case 'shipping_address.hotel_not_supported':
        return new ApplePayError(
          'addressUnserviceable',
          'addressLines',
          error.message,
        );
      default:
        return new ApplePayError('shippingContactInvalid');
    }
  });
};

export const isCancelError = (err: AdyenCheckoutError): boolean => {
  return err.name === 'CANCEL';
};
